"use client";

import React from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { useIsMobileOrTab } from "@/hooks/use-mobile-tab";
import { getImageUrl } from "@/utils/utils";

interface TeamMember {
  id: string;
  name: string;
  position: string;
  image: any;
  biography?: string;
  socialLinks?: {
    linkedin?: string;
    facebook?: string;
    twitter?: string;
  };
}

interface AboutPageManagementProps {
  aboutData: any;
  lang: "en" | "jp";
}

export default function AboutPageManagement({
  aboutData,
  lang,
}: AboutPageManagementProps) {
  const [selectedMember, setSelectedMember] = React.useState<TeamMember | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [modalAnchor, setModalAnchor] = React.useState<{
    bottom: number;
    left: number;
    right: number;
    width: number;
    height: number;
  } | null>(null);

  const isMobileOrTab = useIsMobileOrTab();

  const openBiographyModal = (member: TeamMember) => {
    setSelectedMember(member);
    setIsModalOpen(true);
  };

  const closeBiographyModal = () => {
    setIsModalOpen(false);
    setSelectedMember(null);
  };

  // React.useEffect(() => {
  //   const handleHashChange = () => {
  //     const hash = window.location.hash;

  //     if (hash === "#nippon-koei") {
  //       setTimeout(() => {
  //         setNipponKoeiOpen(true);
  //       }, 500);
  //     } else if (hash === "#ide") {
  //       setTimeout(() => {
  //         setIdeOpen(true);
  //       }, 500);
  //     } else if (hash === "#company-profile" || hash === "") {
  //       setNipponKoeiOpen(false);
  //       setIdeOpen(false);
  //     }
  //   };

  //   const handleInitialHash = () => {
  //     const hash = window.location.hash;
  //     if (hash === "#nippon-koei" || hash === "#ide") {
  //       setTimeout(() => {
  //         handleHashChange();
  //       }, 2000);
  //     } else {
  //       handleHashChange();
  //     }
  //   };

  //   handleInitialHash();
  //   window.addEventListener("hashchange", handleHashChange);

  //   return () => {
  //     window.removeEventListener("hashchange", handleHashChange);
  //   };
  // }, []);

  // Biography Modal Component
  const BiographyModal = () => {
    const modalRef = React.useRef<HTMLDivElement>(null);
    const [modalHeight, setModalHeight] = React.useState(0);
    const [modalLeft, setModalLeft] = React.useState<number | null>(null);

    React.useLayoutEffect(() => {
      if (modalRef.current && modalAnchor && !isMobileOrTab) {
        setModalHeight(modalRef.current.offsetHeight);

        const modalWidth = modalRef.current.offsetWidth;
        const viewportWidth = window.innerWidth;
        let left = modalAnchor.right;
        if (left + modalWidth > viewportWidth) {
          left = Math.max(16, modalAnchor.left - modalWidth);
        }
        setModalLeft(left);
      }
    }, [isModalOpen, selectedMember, modalAnchor, isMobileOrTab]);

    if (!isModalOpen || !selectedMember || (!modalAnchor && !isMobileOrTab))
      return null;

    const style: React.CSSProperties = isMobileOrTab
      ? {
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 1000,
          maxWidth: "100vw",
          minWidth: 0,
          background: "rgba(31,41,55,0.95)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }
      : {
          position: "absolute",
          left: modalLeft ?? 0,
          bottom: modalAnchor.bottom ?? 0,
          zIndex: 1000,
          maxWidth: 900,
        };

    return (
      <div style={style}>
        <motion.div
          className="bg-gray-800 rounded-2xl p-8 max-w-[943px] w-full max-h-[90vh] overflow-y-auto"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
          ref={modalRef}
        >
          <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2.5">
                <h3 className="text-white font-inter text-lg font-medium leading-6 tracking-[-0.72px]">
                  {selectedMember.name}
                </h3>
                <div className="w-[1px] h-4 bg-white"></div>
                <span className="text-gray-50 font-inter text-lg leading-6 tracking-[-0.72px]">
                  {selectedMember.position}
                </span>
              </div>
              {isMobileOrTab && (
                <button
                  onClick={closeBiographyModal}
                  className="text-white hover:text-gray-300 transition-colors p-2"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
            </div>

            {/* Content */}
            <div className="flex flex-col-reverse lg:flex-row items-start gap-7">
              {/* Biography Text */}
              <div className="flex-1 text-gray-50 font-inter text-base leading-7 text-justify">
                {selectedMember.biography || ""}
              </div>

              {/* Image and Social Links */}
              <div className="flex flex-col items-center gap-4 pt-2">
                {/* Photo */}
                <div className="relative w-[140px] h-[150px]">
                  <div className="w-full h-full rounded-[10px] bg-gray-200 absolute"></div>
                  {selectedMember?.image && (
                    <Image
                      src={getImageUrl(selectedMember?.image?.url || "")}
                      alt={`${selectedMember.name} - ${selectedMember.position}`}
                      fill
                      className="object-cover rounded-[10px]"
                      style={{
                        transform: "translate(-1px, 0px)",
                        aspectRatio: "18/19",
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    );
  };

  return (
    <>
      {/* Meet the Management Team Section */}
      <motion.section
        id="management"
        className="relative bg-[#F4F5F6] overflow-hidden py-[50px] px-4 md:px-6 flex items-center justify-center"
        initial={isMobileOrTab ? false : "hidden"}
        animate={isMobileOrTab ? "visible" : undefined}
        whileInView={isMobileOrTab ? undefined : "visible"}
        viewport={isMobileOrTab ? { once: true } : { once: true, amount: 0.3 }}
        variants={
          {
            hidden: { opacity: 0, y: 60 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { duration: 0.8, ease: "easeOut" },
            },
          } as const
        }
        data-section="team"
      >
        <div className="relative z-10 w-full max-w-[1317px] mx-auto overflow-hidden group">
          <div className="w-full bg-white p-8 lg:p-[50px] relative">
            {/* SVG BG above white background */}
            <div className="absolute inset-0 w-full h-full z-10 pointer-events-none flex items-center justify-center opacity-[0.6] mix-blend-luminosity">
              <Image
                src="/images/about/flow_bg.svg"
                alt="Team Section Background"
                fill
                className="object-cover"
                priority={false}
              />
            </div>
            <div className="flex flex-col gap-8 relative z-20">
              {/* Team Header */}
              <div className="flex justify-between items-center pb-4 border-b border-[#F4F5F6]">
                <h2 className="text-gray-900 font-inter text-2xl lg:text-[28px] font-medium leading-[34px] tracking-[-0.72px]">
                  {aboutData?.team?.title}
                </h2>
              </div>

              {/* Team Members */}
              <div className="flex flex-col gap-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {aboutData?.team?.members?.map((member: any) => {
                    return (
                      <motion.div
                        className="flex flex-col rounded-2xl bg-[#E1E0DC] shadow-[0_30px_60px_0_rgba(0,0,0,0.08)] overflow-hidden hover:shadow-[0_40px_80px_0_rgba(0,0,0,0.12)] transition-all duration-300 cursor-pointer group/card h-[400px]"
                        whileHover={{ scale: 1.02 }}
                        key={member?.id}
                      >
                        <div className="relative w-full h-[320px] group-hover/card:h-[220px] bg-[#D9D9D9] overflow-hidden transition-all duration-300">
                          {member?.image && (
                            <Image
                              src={getImageUrl(member?.image?.url || "")}
                              alt={member?.name}
                              fill
                              className="object-cover object-top transition-transform duration-300 group-hover/card:scale-105"
                              style={{
                                aspectRatio: "84/89",
                              }}
                            />
                          )}
                        </div>
                        <div className="flex-1 flex flex-col justify-center items-center gap-2.5 bg-white p-4 relative">
                          <div className="flex flex-col justify-center items-center gap-2">
                            <h4 className="text-gray-900 text-center font-inter text-xl md:text-[24px] font-semibold leading-8 tracking-[-0.72px] line-clamp-1">
                              {member?.name}
                            </h4>
                            <p className="text-gray-800 text-center font-inter text-lg md:text-[20px] font-normal leading-6 tracking-[-0.72px] line-clamp-1">
                              {member?.position}
                            </p>
                            <button
                              onMouseEnter={
                                !isMobileOrTab
                                  ? (e) => {
                                      const btn = e.currentTarget;
                                      const mainElement =
                                        document.getElementById(
                                          "about-main-content"
                                        );

                                      if (btn && mainElement) {
                                        const btnRect =
                                          btn.getBoundingClientRect();
                                        const mainRect =
                                          mainElement.getBoundingClientRect();

                                        const relativeTop =
                                          btnRect.top - mainRect.top;

                                        setModalAnchor({
                                          bottom:
                                            mainElement?.clientHeight -
                                            relativeTop +
                                            18,
                                          left: btnRect.left,
                                          right: btnRect.right,
                                          width: btnRect.width,
                                          height: btnRect.height,
                                        });
                                      }

                                      openBiographyModal({
                                        id: member?.id,
                                        name: member?.name,
                                        position: member?.position,
                                        image: member?.image,
                                        biography: member?.biography,
                                        socialLinks: {
                                          linkedin: "#",
                                          facebook: "#",
                                          twitter: "#",
                                        },
                                      });
                                    }
                                  : undefined
                              }
                              onMouseLeave={
                                !isMobileOrTab ? closeBiographyModal : undefined
                              }
                              onClick={
                                isMobileOrTab
                                  ? (e) => {
                                      openBiographyModal({
                                        id: member?.id,
                                        name: member?.name,
                                        position: member?.position,
                                        image: member?.image,
                                        biography: member?.biography,
                                        socialLinks: {
                                          linkedin: "#",
                                          facebook: "#",
                                          twitter: "#",
                                        },
                                      });
                                    }
                                  : undefined
                              }
                              className={`transition-all duration-300 mt-4 flex w-[150px] py-[8px] px-4 flex-col justify-center items-center gap-1.5 rounded-[20px] border border-[#1C8579] hover:bg-[#1C8579] hover:text-white
                          ${
                            isMobileOrTab
                              ? ""
                              : "opacity-0 group-hover/card:opacity-100"
                          }
                        `}
                            >
                              <div className="flex justify-center items-center gap-2.5 self-stretch">
                                <svg
                                  width="9"
                                  height="14"
                                  viewBox="0 0 9 14"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M7.79098 7.37499L2.91365 12.2107C2.70565 12.4188 2.37231 12.4188 2.20565 12.2107L1.91365 11.9186C1.86475 11.8736 1.82572 11.819 1.79902 11.7581C1.77232 11.6973 1.75854 11.6315 1.75854 11.5651C1.75854 11.4986 1.77232 11.4329 1.79902 11.372C1.82572 11.3112 1.86475 11.2566 1.91365 11.2116L6.16698 7.00013L1.91365 2.83138C1.86454 2.78639 1.82532 2.73167 1.79849 2.67069C1.77167 2.60972 1.75781 2.54383 1.75781 2.47721C1.75781 2.41058 1.77167 2.34469 1.79849 2.28372C1.82532 2.22274 1.86454 2.16802 1.91365 2.12303L2.20698 1.83088C2.37365 1.62278 2.70698 1.62278 2.91498 1.83088L7.83365 6.66663C7.91777 6.76805 7.96017 6.89769 7.95225 7.02925C7.94433 7.1608 7.88667 7.28441 7.79098 7.37499Z"
                                    fill="currentColor"
                                  />
                                </svg>
                                <span className="font-inter text-base font-medium leading-4 capitalize">
                                  {aboutData?.team?.biographyButtonText ||
                                    "Biography"}
                                </span>
                              </div>
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          {/* Progress Bar */}
          <div className="flex h-[2px] bg-gray-25 relative">
            <div className="absolute left-0 top-0 h-[2px] bg-[#1C8579] w-0 group-hover:w-full transition-all duration-500"></div>
          </div>
        </div>
      </motion.section>

      {/* Biography Modal */}
      <BiographyModal />
    </>
  );
}
